import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import BaseAvatar from "components/BaseAvatar";
import providers from "lib/providers";
import { useTranslation } from "locales";
import "./style.scss";

interface Props {
  userId?: string;
  screenName?: string;
  onClose: () => void;
}

function UserProfileModal({ userId, screenName, onClose }: Props) {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserPublicData | null>(null);
  const [loading, setLoading] = useState(true);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setUser(null);

    const load = async () => {
      try {
        if (userId) {
          const u = await providers.UserPublicDataProvider.getById({ userId });
          if (!cancelled) setUser(u);
        } else if (screenName) {
          const result = await providers.UserPublicDataProvider.search({
            data: { relevantTerm: screenName, size: 10 },
          });
          const found =
            result.result?.find((u: UserPublicData) => u.screenName === screenName) ||
            result.result?.[0] ||
            null;
          if (!cancelled) setUser(found);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, screenName]);

  const isOnline = user?.onlineStatus?.isOnline;

  return ReactDOM.createPortal(
    <div className="user-profile-modal__overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={t("User profile")}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <button className="user-profile-modal__close" onClick={onClose} type="button">
          ✕
        </button>
        {loading ? (
          <div className="user-profile-modal__loading">...</div>
        ) : user ? (
          <>
            <div className="user-profile-modal__avatar">
              <BaseAvatar user={user} size={80} />
            </div>
            <div className="user-profile-modal__name">{user.screenName}</div>
            {user.username && (
              <div className="user-profile-modal__username">@{user.username}</div>
            )}
            <div
              className={[
                "user-profile-modal__status",
                isOnline ? "__online" : "",
              ].join(" ")}
            >
              {isOnline ? t("Online") : t("Offline")}
            </div>
          </>
        ) : (
          <div className="user-profile-modal__not-found">{t("User not found")}</div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default UserProfileModal;
